import { EntityTypes, Role, Tenant, User, UserListItem } from "types";
import { getRequestHeaders, handleRes } from "utils";

export interface CheckDuplicateAccountRequestBody {
  token: string;
  email: string;
}

export interface CombineUserEntityAccountsRequestBody {
  email: string;
  roles: Role[];
  entityType: EntityTypes;
  entityID: number;
}
interface SendCombineAccountsEmailRequestBody {
  token: string;
  email: string;
}
export interface UserResponse extends Response {
  user?: User;
}

export interface CheckDuplicateAccountResponse extends Response {
  activeAccount?: boolean;
}
export interface UpdateUserResponse extends Response {
  message?: string;
}

export const getUser = (userID: number): Promise<User> => {
  return fetch(`${window.USER_BASE_URL}/user/${userID}`, {
    method: "GET",
    ...getRequestHeaders(),
  })
    .then((res) => handleRes<User>(res, 200))
    .catch((e) => {
      throw e;
    });
};

export const sendCombineAccountsEmail = async (
  registerUserBody: SendCombineAccountsEmailRequestBody,
): Promise<UserResponse> => {
  return await fetch(
    `${window.USER_BASE_URL}/user/sendEmailToCombineAccounts`,
    {
      method: "POST",
      ...getRequestHeaders(),
      body: JSON.stringify(registerUserBody),
    },
  );
};

export const combineUserEntityAccounts = async (
  _body: CombineUserEntityAccountsRequestBody, // User
): Promise<UserResponse> => {
  return await fetch(`${window.USER_BASE_URL}/user/combineEntityAccounts`, {
    method: "POST",
    ...getRequestHeaders(),
    body: JSON.stringify(_body),
  });
};

export const createUser = async (
  body: string, // User
): Promise<UserResponse> => {
  return await fetch(`${window.USER_BASE_URL}/user`, {
    method: "POST",
    ...getRequestHeaders(),
    body,
  });
};

export const resendRegistrationEmail = async (
  user: User,
): Promise<UserResponse> => {
  return await fetch(
    `${window.USER_BASE_URL}/user/${user.id}/resendRegistrationEmail`,
    {
      method: "POST",
      ...getRequestHeaders(),
      body: JSON.stringify({ email: user.email, resendEmail: !!user.email }),
    },
  );
};

export interface CheckDuplicateAccountRequestParams {
  email: string;
  token: string;
}
export const checkDuplicateAccount = async (
  params: CheckDuplicateAccountRequestParams,
): Promise<CheckDuplicateAccountResponse> => {
  return await fetch(`${window.USER_BASE_URL}/user/checkDuplicateAccount`, {
    method: "POST",
    ...getRequestHeaders(),
    body: JSON.stringify(params),
  });
};

interface UpdateUserParams {
  userID: number | string | undefined;
  body: string; // User
}
export const updateUser = async ({
  body,
  userID,
}: UpdateUserParams): Promise<UpdateUserResponse> => {
  return await fetch(`${window.USER_BASE_URL}/user/${userID}`, {
    method: "PUT",
    ...getRequestHeaders(),
    body,
  });
};

interface GetUserTenantsParams {
  userID: number | string | undefined;
  includeDisabled: boolean;
}
export const getUserTenants = ({
  userID,
  includeDisabled,
}: GetUserTenantsParams): Promise<Tenant[]> => {
  return fetch(
    `${window.USER_BASE_URL}/user/${userID}/tenant${
      includeDisabled ? "?includeDisabled=true" : ""
    }`,
    {
      method: "GET",
      ...getRequestHeaders(),
    },
  )
    .then((res) => handleRes<Tenant[]>(res, 200))
    .catch((e) => {
      return [];
    });
};

export const getTenantUsers = (
  id: number | string | undefined,
): Promise<User[]> => {
  return fetch(`${window.USER_BASE_URL}/tenant/${id}/users`, {
    method: "GET",
    ...getRequestHeaders(),
  })
    .then((res) => handleRes<User[]>(res, 200))
    .catch((e) => {
      return [];
    });
};

export const getTenantUserList = (
  id: number | string | undefined,
): Promise<UserListItem[]> => {
  return fetch(`${window.USER_BASE_URL}/tenant/${id}/users/list`, {
    method: "GET",
    ...getRequestHeaders(),
  })
    .then((res) => handleRes<UserListItem[]>(res, 200))
    .catch((e) => {
      return [];
    });
};

export const getAllUsers = (): Promise<User[]> => {
  return fetch(`${window.USER_BASE_URL}/user`, {
    method: "GET",
    ...getRequestHeaders(),
  })
    .then((res) => handleRes<User[]>(res, 200))
    .catch((e) => {
      throw e;
    });
};

export const getSuperUsers = (): Promise<User[]> => {
  return fetch(`${window.USER_BASE_URL}/user/super`, {
    method: "GET",
    ...getRequestHeaders(),
  })
    .then((res) => handleRes<User[]>(res, 200))
    .catch((e) => {
      throw e;
    });
};

interface GetPropertyUsersParams {
  tenantID: number | string | undefined;
  propertyID: number | string | undefined;
  includeGroupUsers?: boolean;
  includeInactive?: boolean;
}
export const getPropertyUsers = ({
  tenantID,
  propertyID,
  includeGroupUsers = false,
  includeInactive = false,
}: GetPropertyUsersParams): Promise<User[]> => {
  const url = new URL(`${window.PROPERTY_BASE_URL}/tenant/${tenantID}/properties/${propertyID}/users`);
  const params = new URLSearchParams();
  params.append('includeGroupUsers', includeGroupUsers.toString());
  params.append('includeInactive', includeInactive.toString());
  url.search = params.toString();

  return fetch(
    url.toString(),
    {
      method: "GET",
      ...getRequestHeaders(),
    },
  )
    .then((res) => handleRes<User[]>(res, 200))
    .catch((e) => {
      throw e;
    });
};

interface PutPropertyUsersParams {
  tenantID: number | string | undefined;
  propertyID: number | string | undefined;
  body: string; // Users[] as JSON string
}
export const putPropertyUsers = async ({
  tenantID,
  propertyID,
  body,
}: PutPropertyUsersParams): Promise<Response> => {
  return await fetch(
    `${window.PROPERTY_BASE_URL}/tenant/${tenantID}/properties/${propertyID}/users`,
    {
      method: "PUT",
      ...getRequestHeaders(),
      body,
    },
  );
};

interface PutPropertyGroupUsersParams {
  tenantID: number | string | undefined;
  groupID: number | string | undefined;
  body: string; // Users[] as JSON string
}
export const putPropertyGroupUsers = async ({
  tenantID,
  groupID,
  body,
}: PutPropertyGroupUsersParams): Promise<Response> => {
  return await fetch(
    `${window.PROPERTY_BASE_URL}/tenant/${tenantID}/propertyGroups/${groupID}/users`,
    {
      method: "PUT",
      ...getRequestHeaders(),
      body,
    },
  );
};

export const softDeleteUser = async (userID: number | string | undefined): Promise<Response> => {
  return await fetch(`${window.USER_BASE_URL}/user/${userID}`, {
    method: "DELETE",
    ...getRequestHeaders(),
  });
};